// PostHog analytics initialization via posthog-js
// Project key and region are synced by the PostHog connector.
//
// Consent: PostHog is never initialized until Experience Analytics consent exists
// (and only on the production host). Withdrawal opts out immediately, stops
// replay and clears PostHog browser persistence.

import posthog from 'posthog-js';
import { isProductionHost } from './env';

const projectToken = import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY as string | undefined;
const region = (import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_REGION as string | undefined) || 'eu';
const apiHost = region === 'us' ? 'https://us.i.posthog.com' : 'https://eu.i.posthog.com';

let initialized = false;
let active = false;

/** Returns true when PostHog was started by this call (first time). */
export function initPostHog(): boolean {
  if (!projectToken || !isProductionHost()) return false;
  if (initialized) {
    if (!active) resumePostHog();
    return false;
  }
  initialized = true;
  active = true;

  posthog.init(projectToken, {
    api_host: apiHost,
    // We capture pageviews manually on route changes (SPA) to avoid double-counting
    capture_pageview: false,
    capture_pageleave: true,
    session_recording: {
      // Default masking: sensitive inputs are not recorded
      maskAllInputs: true,
    },
  });
  return true;
}

function resumePostHog() {
  active = true;
  posthog.set_config({ disable_persistence: false, disable_session_recording: false, autocapture: true });
  posthog.opt_in_capturing({ captureEventName: false });
  posthog.startSessionRecording();
}

export function disablePostHog() {
  if (!initialized || !active) return;
  active = false;
  try {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
    posthog.set_config({ disable_session_recording: true, autocapture: false, disable_persistence: true });
  } catch {
    /* ignore */
  }
  clearPostHogStorage();
}

/** Remove PostHog identifiers/persistence; the opt-out flag itself is kept. */
export function clearPostHogStorage() {
  try {
    for (const store of [localStorage, sessionStorage]) {
      Object.keys(store)
        .filter((k) => k.startsWith('ph_') || (k.startsWith('__ph') && !k.startsWith('__ph_opt_in_out')))
        .forEach((k) => store.removeItem(k));
    }
    const host = window.location.hostname.replace(/^www\./, '');
    document.cookie.split(';').forEach((part) => {
      const name = part.split('=')[0].trim();
      if (name.startsWith('ph_')) {
        for (const domain of ['', `; domain=.${host}`, `; domain=${window.location.hostname}`]) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
        }
      }
    });
  } catch {
    /* ignore */
  }
}

export function isPostHogActive() {
  return initialized && active;
}

export function trackPostHogPageView(path: string) {
  if (!projectToken || !initialized || !active) return;
  posthog.capture('$pageview', { $current_url: window.location.origin + path });
}
